import { useEffect, useRef, useState } from "react";
import { KiriCharacter } from "@/components/kiri/KiriCharacter";
import { ChevronRight, SkipForward, X } from "lucide-react";

// The embedded "live screen" — the real Fieldlink app running same-origin in
// an iframe, styled like a screen-share window. Kiri narrates and highlights
// real elements INSIDE this frame; the customer's actual browser never
// leaves the onboarding session page. Real clicks inside the frame still
// work (it's the live app, not a screenshot) so the customer can try things
// themselves without losing Kiri.
export function OnboardingLivePreview({ view, onFinish }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [searching, setSearching] = useState(false);
  const iframeRef = useRef(null);
  const loadedKeyRef = useRef(null);

  const tour = view?.tour || null;
  const step = tour?.steps?.[stepIdx];

  useEffect(() => {
    if (!view) return;
    const key = tour ? tour.tour_id : view.route;
    if (loadedKeyRef.current !== key) {
      loadedKeyRef.current = key;
      setStepIdx(0);
      if (iframeRef.current) iframeRef.current.src = tour ? tour.route : view.route;
    }
  }, [view, tour]);

  useEffect(() => {
    if (!tour || !step) { setRect(null); return; }
    let cancelled = false;
    let tries = 0;
    setSearching(true);
    const poll = setInterval(() => {
      if (cancelled) return;
      const doc = iframeRef.current?.contentDocument;
      const el = doc?.querySelector(`[data-tour-id="${step.target}"]`);
      tries += 1;
      if (el) {
        const frameBox = iframeRef.current.getBoundingClientRect();
        const elBox = el.getBoundingClientRect();
        setRect({ top: frameBox.top + elBox.top, left: frameBox.left + elBox.left, width: elBox.width, height: elBox.height });
        setSearching(false);
        clearInterval(poll);
        if (step.advance_on === "click") {
          const onClick = () => advance();
          el.addEventListener("click", onClick, { once: true });
        }
      } else if (tries > 40) {
        clearInterval(poll);
        setSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearInterval(poll); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tour, stepIdx]);

  const advance = () => {
    if (!tour) return;
    if (stepIdx + 1 >= tour.steps.length) { onFinish("completed"); return; }
    setStepIdx((i) => i + 1);
  };

  if (!view) return null;

  return (
    <div className="relative w-full h-full flex flex-col" data-testid="onboarding-live-preview">
      <div className="flex items-center gap-2 px-3 py-2 bg-[#1A1F27] rounded-t-lg border border-white/10">
        <span className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
        <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
        <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/70" />
        <span className="ml-2 text-[10px] font-mono text-zinc-500 truncate">fieldlink.co.nz{tour ? tour.route : view.route}</span>
        <span className="ml-auto flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-emerald-400" data-testid="preview-driving-badge">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Kiri is showing you this
        </span>
      </div>
      <div className="relative flex-1 rounded-b-lg overflow-hidden border border-white/10 border-t-0 bg-white">
        <iframe ref={iframeRef} title="Fieldlink live preview" className="w-full h-full" data-testid="onboarding-live-iframe" />
        {rect && (
          <div className="fixed rounded-lg pointer-events-none transition-all duration-200 z-[60]"
            style={{ top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12,
              border: "2px solid #FF5F15", boxShadow: "0 0 0 6px rgba(255,95,21,0.22), 0 0 0 9999px rgba(0,0,0,0.35)" }} />
        )}
      </div>
      <div className="absolute bottom-3 left-3 right-3 flex justify-center pointer-events-none">
        <div className="pointer-events-auto flex items-start gap-3 bg-[#12161C]/95 border border-white/10 rounded-xl shadow-2xl p-3 w-full max-w-md" data-testid="preview-caption-card">
          <KiriCharacter state={searching ? "searching" : "attention"} size="sm" />
          <div className="flex-1 min-w-0">
            {tour ? (
              <>
                <p className="text-[10px] font-mono uppercase tracking-widest text-[#FF5F15] font-bold">{step?.title}</p>
                <p className="text-sm text-zinc-200 mt-0.5 leading-snug">{searching ? "Looking for that on this screen…" : step?.text}</p>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500" data-testid="preview-step-counter">{stepIdx + 1} OF {tour.steps.length}</span>
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => onFinish("skipped")} className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 hover:text-white px-2 py-1" data-testid="preview-skip-btn">
                      <SkipForward className="w-3 h-3 inline mr-1" />Skip
                    </button>
                    <button onClick={advance} className="text-[10px] font-bold uppercase tracking-wider bg-[#FF5F15] hover:bg-[#E65100] text-white rounded-md px-2.5 py-1 flex items-center gap-1" data-testid="preview-next-btn">
                      {stepIdx + 1 >= tour.steps.length ? "Finish" : "Next"} <ChevronRight className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <p className="text-[10px] font-mono uppercase tracking-widest text-[#FF5F15] font-bold">Here's that page</p>
                <p className="text-sm text-zinc-200 mt-0.5 leading-snug">Have a look around — I'm still here if you want to ask anything.</p>
                <button onClick={() => onFinish("acknowledged")} className="mt-2 text-[10px] font-bold uppercase tracking-wider bg-[#FF5F15] hover:bg-[#E65100] text-white rounded-md px-2.5 py-1 flex items-center gap-1" data-testid="preview-back-btn">
                  Back to Kiri <X className="w-3 h-3" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
